import { useState } from 'react';
import type { GenerateProductCategorySuggestionsResponseDto } from '@tiendanube/common';
import { AiFeature } from '@tiendanube/common/src/enums';
import { FEATURE_AI_TIER } from 'App/features';
import { useToast, useTranslationCommon } from 'commons/hooks';
import { logError } from 'commons/logger';
import { useAiUsage } from 'domains/Ai/hooks';
import { useGetLanguage } from 'domains/Auth/hooks';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useGetCategories } from 'domains/Catalog/Categories/hooks';
import { useGenerateProductCategorySuggestions } from 'domains/Catalog/Products/pages/hooks';
import { RequiredAiProperties } from '../../Variants/types';

interface UseProductCategorySuggestionsWithAIProps {
  selectedCategories: string[];
  requiredAiProperties: RequiredAiProperties;
  onCategoriesGenerated?: (
    response: GenerateProductCategorySuggestionsResponseDto,
  ) => void;
}

function useProductCategorySuggestionsWithAI({
  selectedCategories,
  requiredAiProperties,
  onCategoriesGenerated,
}: Readonly<UseProductCategorySuggestionsWithAIProps>) {
  const tCommon = useTranslationCommon();
  const defaultLanguage = useGetLanguage();
  const categories = useGetCategories();
  const { addToast } = useToast();

  const [isGeneratingAI, setIsGeneratingAI] = useState(false);

  const { generateCategorySuggestions } =
    useGenerateProductCategorySuggestions();

  const { requiredTier, aiUsesUpsellTitle, refetchAiUses } = useAiUsage();

  const generateCategorySuggestionsWithAI = async () => {
    const hasNameAndDescription =
      requiredAiProperties?.name?.[defaultLanguage]?.trim() !== '' &&
      requiredAiProperties?.description?.[defaultLanguage]?.trim() !== '';

    if (isGeneratingAI || !hasNameAndDescription) return;

    setIsGeneratingAI(true);

    try {
      const requestData = {
        product: {
          name: requiredAiProperties.name?.[defaultLanguage] || '',
          description:
            requiredAiProperties.description?.[defaultLanguage] || '',
          ...(requiredAiProperties.imageUrl && {
            image_url: requiredAiProperties.imageUrl,
          }),
        },
        existing_categories: selectedCategories
          .map((categoryId) => {
            const category = categories.find((cat) => cat.id === categoryId);
            return category?.title || '';
          })
          .filter(Boolean),
        lang: defaultLanguage,
      };

      const response = await generateCategorySuggestions(requestData);

      if (onCategoriesGenerated) {
        onCategoriesGenerated(response);
      }
      refetchAiUses();
    } catch (error) {
      logError('Error generating category suggestions with AI', error);
      addToast({
        appearance: 'danger',
        label: tCommon('aiGeneration.errorGenerating'),
      });
    } finally {
      setIsGeneratingAI(false);
    }
  };

  const handleGenerateWithAI = useUpsellFlow({
    title: aiUsesUpsellTitle,
    featureKey: FEATURE_AI_TIER,
    trackingSource: AiFeature.AI_PRODUCT_CATEGORY_SUGGESTIONS,
    callback: generateCategorySuggestionsWithAI,
    minValue: requiredTier,
  });

  const hasCategoriesSelected = !!selectedCategories.length;

  const getTooltipText = (): string | undefined => {
    if (isGeneratingAI) {
      return tCommon('aiGeneration.generating.categories');
    }

    const hasNameAndDescription =
      requiredAiProperties?.name?.[defaultLanguage]?.trim() !== '' &&
      requiredAiProperties?.description?.[defaultLanguage]?.trim() !== '';

    if (!hasNameAndDescription) {
      return tCommon('aiGeneration.addNameAndDescriptionToGenerate');
    }

    return undefined;
  };

  return {
    isGeneratingAI,
    handleGenerateWithAI,
    hasCategoriesSelected,
    getTooltipText,
  };
}

export default useProductCategorySuggestionsWithAI;
